import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { CommittedRowSchema } from '../../shared/bulk-commit.schema';
import { ObjectIdSchema } from '../../shared/object-id.schema';
/**
 * Bulk Add User Input Schema
 * Input format for adding users in bulk with assigned departments and roles
 */ export const UserInputSchema = z.strictObject({
    firstName: z.string(),
    lastName: z.string(),
    email: z.email(),
    department: z.string(),
    role: z.string(),
    roleDescription: z.string().optional()
});
/**
 * Update User Input Schema
 * In-place edit of a wizard-seeded user, matched by `_id`
 * (ADR 0011, second amendment)
 */ export const UpdateUserInputSchema = UserInputSchema.safeExtend({
    _id: ObjectIdSchema
});
/**
 * Add Users Bulk Request Schema
 * POST /api/v2/initialize/users
 * Allows bulk creation of users with assigned departments and roles
 */ export const InitializeAddUsersRequestSchema = z.object({
    users: z.array(UserInputSchema),
    // In-place edits of already-seeded rows, matched by `_id`. Non-destructive:
    // later steps keep their progress (ADR 0011, second amendment).
    updates: z.array(UpdateUserInputSchema).optional(),
    // Surgical deletion of already-seeded rows by `_id`. Optional references
    // (org-function lead) are cleared, managed departments demote to temps,
    // required refs (system/process ownership) block the removal
    // (ADR 0011, third amendment).
    removals: z.array(ObjectIdSchema).optional()
}).refine((v)=>v.users.length > 0 || !!v.updates?.length || !!v.removals?.length, {
    message: 'at least one of users, updates or removals is required',
    path: [
        'users'
    ]
}).refine((v)=>!v.updates?.some((update)=>v.removals?.includes(update._id)), {
    message: 'an id cannot appear in both updates and removals',
    path: [
        'removals'
    ]
});
/**
 * Add Users Bulk Response Schema
 * One result per request `users` row, in request order; a row whose email
 * already exists is skipped and carries the existing user's id.
 */ export const InitializeAddUsersResponseSchema = z.object({
    users: z.array(CommittedRowSchema)
});
/**
 * Add Users Bulk Error Schema
 * Returned when user creation fails
 */ export const InitializeAddUsersErrorSchema = BaseErrorSchema;

//# sourceMappingURL=initialize-add-users.schema.js.map